import Instance from "@/types/Instance"
import {LaneOption} from "@/types/ColoredOption"
import {isWithin, KanbanPeriods} from "@/utils/finishedPeriod"
import {
    Coordinate, coordinateValues, CoordinateValue, Dimension, inCoordinate, NO_VALUE_ID
} from "@/utils/kanbanDimensions"

/**
 * Como os itens do Kanban se distribuem: em blocos, e dentro de cada bloco, em raias.
 *
 * A primeira dimensão vira as raias; as demais viram blocos, um por combinação de valores.
 * Sem dimensão posterior existe exatamente um bloco, sem rótulo — e o quadro é o de sempre.
 *
 * Puro: sem Vue, sem storage, sem requisição. Era o `regroup` de dentro do tabuleiro, copiado
 * no sandbox de desenvolvimento; agora os dois chamam a mesma função.
 */

export type LaneModel = LaneOption & {
    instances: Instance[]
    /** Quantos o prazo escondeu. Entra no contador da faixa e na conta do bloco vazio. */
    hiddenCount: number
}

/**
 * Um par rótulo/valor do cabeçalho de um bloco.
 *
 * Leva o `related` junto porque o chip de um valor de relacionamento é clicável, e para abrir
 * a ficha ele precisa do item resolvido — o mesmo objeto do `_rels` que o chip do card recebe.
 * A dimensão já o tem em mão; pedi-lo de novo seria procurar o que já está achado.
 */
/** O rótulo de um bloco: um valor da coordenada dele — ver o `CoordinateValue`. */
export type BlockLabel = CoordinateValue

export type BlockModel = {
    /** Identidade estável para o `:key`; string vazia no bloco único. */
    key: string
    /** Os valores das dimensões posteriores. Vazio quando não há nenhuma. */
    coordinate: Coordinate
    labels: BlockLabel[]
    lanes: LaneModel[]
}

/**
 * Um bloco sem nada para mostrar.
 *
 * Função lida a cada desenho, e não campo gravado no agrupamento: o arrasto NÃO reagrupa — o
 * vuedraggable tira o card da lista de uma raia e põe na outra —, e um booleano calculado no
 * agrupamento ficaria velho na hora. O bloco que acabou de esvaziar continuaria afirmando que
 * tem card e ficaria na tela mesmo com os vazios escondidos.
 *
 * Conta o que o prazo escondeu: um bloco cujos únicos itens estão fora do prazo ainda precisa
 * aparecer, senão some junto o menu que os traria de volta.
 */
export function isEmptyBlock(block: BlockModel): boolean {
    return block.lanes.every(lane => !lane.instances.length && !lane.hiddenCount)
}

export function coordinateKey(coordinate: Coordinate): string {
    return Object.keys(coordinate).sort()
        .map(key => `${key}=${coordinate[key]}`)
        .join('|')
}

/**
 * As raias de um conjunto de itens.
 *
 * O prazo só esconde onde a raia é terminal — e só a dimensão de situação produz raia
 * terminal, porque só ela tem o carimbo de quando o item entrou ali.
 */
export function laneModels(
    instances: Instance[], dimension: Dimension, visible: number[], periods: KanbanPeriods
): LaneModel[] {
    return dimension.values
        .filter(value => visible.includes(value.id))
        .map(value => {
            //includes, e não igualdade: numa dimensão múltipla o mesmo item entra em cada raia
            //a que pertence — é o que mostra que a demanda está em dois projetos.
            const all = instances.filter(instance => dimension.valuesOf(instance).includes(value.id))
            const period = periods[value.id]
            const shown = (value.final && period) ? all.filter(i => isWithin(i, period)) : all
            return {...value, instances: shown, hiddenCount: all.length - shown.length}
        })
}

/**
 * Os blocos do quadro: o produto cartesiano das dimensões de 2 em diante.
 *
 * Um item entra no bloco cuja coordenada casa com a dele em TODAS as dimensões posteriores.
 * Valor ausente conta como o grupo "Não definido", que é a coordenada `undefined`.
 */
export function blockModels(
    instances: Instance[], dimensions: Dimension[], visible: VisibleValues, periods: KanbanPeriods
): BlockModel[] {
    const [lane, ...rest] = dimensions
    if (!lane) return []
    const laneVisible = visible[lane.key] || lane.values.map(value => value.id)
    return combinations(rest, visible).map(coordinate => {
        const mine = rest.length
            ? instances.filter(instance => inCoordinate(instance, coordinate, rest))
            : instances
        const lanes = laneModels(mine, lane, laneVisible, periods)
        return {
            key: coordinateKey(coordinate),
            coordinate,
            //A opção sempre existe: a coordenada foi montada a partir dos valores da dimensão.
            //O `related` só vem em relacionamento, e é `undefined` no grupo "Não definido" —
            //é o que faz o rótulo cair no chip comum em vez de tentar abrir uma ficha que não há.
            labels: coordinateValues(coordinate, rest),
            lanes
        }
    })
}

/** Os valores visíveis de cada dimensão, por chave. Dimensão ausente = todos visíveis. */
export type VisibleValues = { [dimensionKey: string]: number[] }

/**
 * A escolha de valores de uma dimensão, pronta para guardar.
 *
 * **A escolha igual ao PADRÃO guarda-se como AUSÊNCIA**, e não como lista. A lista seria um
 * retrato dos valores de HOJE: a opção criada amanhã, a situação acrescentada ao fluxo — nenhuma
 * estaria nela, e nasceriam escondidas sem que ninguém as tivesse escondido. Ausente é o mesmo
 * "nunca escolhi", e ele acompanha a dimensão quando ela cresce.
 *
 * Era "todos guarda ausência", e continua sendo exatamente isso onde o padrão é todos — situação
 * e seleção. Na dimensão de relacionamento o padrão são os valores COM item (ver
 * `Dimension.defaultValues`), e aí a diferença aparece: marcar todos passa a guardar a lista,
 * porque marcar todos é uma escolha, e sem guardá-la ela se desfaria na próxima abertura.
 *
 * O preço é não distinguir "quero o padrão" de "nunca escolhi" — e não há o que distinguir: as
 * duas pedem a mesma coisa da tela, agora e depois.
 */
export function withValues(
    visible: VisibleValues, dimension: Dimension, values: number[]
): VisibleValues {
    const next = {...visible}
    if (sameValues(values, defaultValues(dimension))) delete next[dimension.key]
    else next[dimension.key] = values
    return next
}

/** Os mesmos valores, em qualquer ordem — é o que decide se a escolha é o padrão. */
function sameValues(a: number[], b: number[]): boolean {
    return a.length === b.length && a.every(id => b.includes(id))
}

/**
 * O mapa inteiro pela mesma regra, para o que chega pronto de uma tela.
 *
 * O modal de dimensões manda o mapa completo, com uma entrada por dimensão — inclusive as que
 * estão no padrão. Sem passar por aqui, abrir o modal e salvar sem tocar em nada já congelaria
 * esse padrão na lista de hoje.
 */
export function storedValues(visible: VisibleValues, dimensions: Dimension[]): VisibleValues {
    return dimensions.reduce((kept, dimension) =>
        withValues(kept, dimension, kept[dimension.key] || defaultValues(dimension)), {...visible})
}

/** Todos os valores de uma dimensão. */
export function allValues(dimension: Dimension): number[] {
    return dimension.values.map(value => value.id)
}

/**
 * Os valores à vista para quem nunca escolheu.
 *
 * Todos, menos onde a dimensão diz o contrário — hoje só o relacionamento, que traz os
 * candidatos sem item junto e não quer abrir o quadro com uma raia vazia para cada um.
 */
export function defaultValues(dimension: Dimension): number[] {
    return dimension.defaultValues || allValues(dimension)
}

/**
 * A ordem e o conjunto de grupos como foram desenhados na CARGA.
 *
 * Só o relacionamento precisa: as situações de um fluxo e as opções de um domínio têm a ordem
 * que o usuário desenhou, e ela não depende de contagem nenhuma.
 */
export type FrozenGroups = { order: LaneOption[]; shown: number[] }

/**
 * Tira a foto. O par com o `withFrozenGroups`, que a aplica.
 *
 * Guarda os GRUPOS, e não os ids: um valor cujo último card saiu desaparece dos valores da
 * dimensão — ela os tira dos itens presentes —, e sem o nome e a cor não haveria como desenhar
 * de volta a raia que ficou vazia.
 */
export function frozenGroupsOf(dimension: Dimension): FrozenGroups {
    return {order: dimension.values, shown: defaultValues(dimension)}
}

/**
 * A dimensão com a ordem e o conjunto de grupos da carga, e não os de agora.
 *
 * O relacionamento ordena as raias por QUANTIDADE e mostra por padrão as que têm item — as
 * duas coisas tiradas dos itens presentes. Isso é o que se quer ao abrir o quadro, e é o
 * contrário do que se quer depois: mover um card mudava a contagem, e a raia saltava de lugar
 * debaixo do cursor ou desaparecia por ter ficado vazia. Um quadro em que o chão se move não
 * dá para trabalhar — a comparação que o Kanban serve a fazer depende de as colunas estarem
 * onde estavam.
 *
 * **A foto só cresce.** Um valor que ganha item durante a sessão APARECE, ainda que não
 * estivesse nela: é o caso de mandar um card para uma raia que não tinha nenhum, pelo modal, e
 * sem isso o card sumiria da tela. Nada sai — nem a raia que ficou vazia, que é o ponto.
 *
 * O grupo "Não definido" fica sempre no fim, como em qualquer dimensão: não é um valor entre os
 * valores, é a ausência deles.
 */
export function withFrozenGroups(dimension: Dimension, frozen?: FrozenGroups): Dimension {
    if (!frozen) return dimension
    const now = new Map(dimension.values.map(value => [value.id, value]))
    const inPhoto = new Set(frozen.order.map(value => value.id))
    //O grupo de agora quando ele ainda existe — o identificador do item pode ter mudado —, e o
    //da foto quando não: raia esvaziada continua sendo raia.
    const fromPhoto = frozen.order.filter(value => value.id !== NO_VALUE_ID)
        .map(value => now.get(value.id) || value)
    //Os que nasceram depois da foto entram atrás dos dela, na ordem em que a dimensão os trouxe
    const newcomers = dimension.values.filter(value =>
        value.id !== NO_VALUE_ID && !inPhoto.has(value.id))
    const noValue = [...dimension.values, ...frozen.order].find(value => value.id === NO_VALUE_ID)
    return {
        ...dimension,
        values: [...fromPhoto, ...newcomers, ...(noValue ? [noValue] : [])],
        defaultValues: [
            ...frozen.shown,
            ...defaultValues(dimension).filter(id => !frozen.shown.includes(id))
        ]
    }
}

/**
 * Onde um card foi solto: o evento cru do vuedraggable, mais o bloco e a raia que o
 * receberam. O par é o que permite ao tabuleiro montar o alvo completo sem adivinhar.
 */
export type CardDrop = { event: any, coordinate: Coordinate, lane: LaneModel }

/**
 * Todas as combinações de valores das dimensões dadas, na ordem delas.
 *
 * Sem dimensão nenhuma devolve UMA combinação vazia, e não nenhuma: é o bloco único do quadro
 * de uma dimensão só. Devolver lista vazia aqui apagaria o quadro inteiro.
 */
function combinations(dimensions: Dimension[], visible: VisibleValues): Coordinate[] {
    return dimensions.reduce((acc: Coordinate[], dimension) => {
        const allowed = visible[dimension.key]
        const values = dimension.values.filter(value => !allowed || allowed.includes(value.id))
        return acc.flatMap(coordinate =>
            values.map(value => ({...coordinate, [dimension.key]: value.id})))
    }, [{}])
}
